<template>
  <div class="container-fluid p-4 event-show">
    <div v-if="data" class="event-content">
      <!-- Event outline (directly on the page background, like the design) -->
      <div class="outline-section">
        <div class="outline-header">
          <div class="outline-heading">
            <h1 class="event-title">{{ data.event.title }}</h1>
            <div v-if="data.event.primary_organizer_name && data.event.primary_organizer_name !== '-'" class="organizer-name">{{ data.event.primary_organizer_name }}</div>
          </div>
          <router-link
            v-if="canEdit"
            :to="{ name: 'master.events.edit', params: { id: data.event.id } }"
            class="btn btn-sm edit-button"
          >
            {{ $t('eventDetail.edit') }}
          </router-link>
        </div>
        <div v-if="data.event.description">
          <p class="event-description" :class="{ collapsed: !descriptionExpanded }">{{ data.event.description }}</p>
          <a
            v-if="isDescriptionLong"
            href="#"
            class="description-toggle"
            @click.prevent="descriptionExpanded = !descriptionExpanded"
          >
            {{ descriptionExpanded ? $t('eventDetail.showLess') : $t('eventDetail.showMore') }}
          </a>
        </div>

      </div>

      <!-- Info cards on the same 12-column grid as the KPI row below -->
      <div class="row g-3 mt-1">
        <div class="col-md-6">
          <div class="info-card">
            <div class="info-label">{{ $t('eventDetail.eventDate') }}</div>
            <div class="info-value">
              <span v-for="(part, i) in textParts(dateRangeLabel)" :key="i" :class="part.isNum ? 'num' : 'unit'">{{ part.text }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="info-card">
            <div class="info-label">{{ $t('eventDetail.fee') }}</div>
            <div class="info-value">
              <span v-for="(part, i) in textParts(feeLabel)" :key="i" :class="part.isNum ? 'num' : 'unit'">{{ part.text }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="info-card">
            <div class="info-label">{{ $t('eventDetail.capacity') }}</div>
            <div class="info-value">
              <span v-for="(part, i) in textParts(capacityLabel)" :key="i" :class="part.isNum ? 'num' : 'unit'">{{ part.text }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- KPI cards -->
      <div class="row g-3 mt-1">
        <div class="col-md-3 col-6">
          <KpiCard
            :title="$t('eventDetail.bookingRate')"
            :value="data.booking_rate"
            v-bind="kpiTrend(data.booking_rate, data.benchmark?.booking_rate)"
          />
        </div>
        <div class="col-md-3 col-6">
          <KpiCard
            :title="$t('eventDetail.accommodationGuests')"
            :value="data.accommodation_guests_percentage"
            v-bind="kpiTrend(data.accommodation_guests_percentage, data.benchmark?.accommodation_guests_percentage)"
          />
        </div>
        <div class="col-md-3 col-6">
          <KpiCard
            :title="$t('eventDetail.repeaters')"
            :value="data.repeater_percentage"
            v-bind="kpiTrend(data.repeater_percentage, data.benchmark?.repeater_percentage)"
          />
        </div>
        <div class="col-md-3 col-6">
          <KpiCard
            :title="$t('eventDetail.stockholders')"
            :value="data.stockholder_percentage"
            v-bind="kpiTrend(data.stockholder_percentage, data.benchmark?.stockholder_percentage)"
          />
        </div>
      </div>

      <!-- Demographics -->
      <div class="row g-3 mt-1">
        <div class="col-md-6">
          <DonutLegendCard
            :title="$t('eventDetail.ageGroups')"
            :items="ageGroupItems"
            :comparisonTitle="$t('eventDetail.distributionComparison')"
            :comparisonRows="ageComparisonRows"
          />
        </div>
        <div class="col-md-6">
          <DonutLegendCard
            :title="$t('eventDetail.genders')"
            :items="genderItems"
            :comparisonTitle="$t('eventDetail.distributionComparison')"
            :comparisonRows="genderComparisonRows"
          />
        </div>
      </div>

      <!-- Residential areas -->
      <div class="row g-3 mt-1 mb-3">
        <div class="col-12">
          <ResidenceBarCard
            :title="$t('eventDetail.residentialAreas')"
            :areas="data.residential_areas"
            :emptyLabel="$t('eventDetail.noData')"
          />
        </div>
      </div>
    </div>

    <div v-else class="text-center mt-5">
      <div v-if="isLoading">{{ $t('eventDetail.loading') }}</div>
      <div v-else>{{ $t('eventDetail.notFound') }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { colors } from '../../../../consts/colors';
import { usePermission } from '../../../composables/usePermission';
import KpiCard from '../../../components/user/statistics/KpiCard.vue';
import DonutLegendCard from '../../../components/user/statistics/DonutLegendCard.vue';
import ResidenceBarCard from '../../../components/user/statistics/ResidenceBarCard.vue';

const { t: $t } = useI18n();
const route = useRoute();
const { hasEditPermission } = usePermission();

const data = ref(null);
const isLoading = ref(true);

const canEdit = computed(() => hasEditPermission());

const ageGroupDefinitions = [
  { key: 'less_20', labelKey: 'guestDemographics.age_less_20', color: colors.blue[500] },
  { key: '20', labelKey: 'guestDemographics.age_20', color: colors.blue[400] },
  { key: '30', labelKey: 'guestDemographics.age_30', color: colors.blue[300] },
  { key: '40', labelKey: 'guestDemographics.age_40', color: colors.blue[200] },
  { key: '50', labelKey: 'guestDemographics.age_50', color: colors.blue[100] },
  { key: 'more_60', labelKey: 'guestDemographics.age_more_60', color: colors.blue[50] },
  { key: 'other', labelKey: 'guestDemographics.age_other', color: colors.gray[200] },
];

const genderDefinitions = [
  { key: 'male', labelKey: 'guestDemographics.male', color: colors.blue[500] },
  { key: 'female', labelKey: 'guestDemographics.female', color: colors.blue[400] },
  { key: 'other', labelKey: 'guestDemographics.no_answer', color: colors.blue[300] },
];

const descriptionExpanded = ref(false);
const isDescriptionLong = computed(() => (data.value?.event?.description?.length ?? 0) > 160);

const ageItemsFrom = (list) => ageGroupDefinitions.map(def => ({
  key: def.key,
  label: $t(def.labelKey),
  value: list?.find(item => item.age_group === def.key)?.percentage ?? 0,
  color: def.color,
}));

const genderItemsFrom = (list) => genderDefinitions.map(def => ({
  key: def.key,
  label: $t(def.labelKey),
  value: list?.find(item => item.gender === def.key)?.percentage ?? 0,
  color: def.color,
}));

const ageGroupItems = computed(() => ageItemsFrom(data.value?.age_groups));
const genderItems = computed(() => genderItemsFrom(data.value?.genders));

const ageComparisonRows = computed(() => data.value ? [
  { label: $t('eventDetail.participants'), items: ageGroupItems.value },
  { label: $t('eventDetail.allEvents'), items: ageItemsFrom(data.value.benchmark?.age_groups) },
] : []);

const genderComparisonRows = computed(() => data.value ? [
  { label: $t('eventDetail.participants'), items: genderItems.value },
  { label: $t('eventDetail.allEvents'), items: genderItemsFrom(data.value.benchmark?.genders) },
] : []);

/**
 * 全イベント平均との差分(ポイント)を KpiCard のバッジ用に変換
 */
const kpiTrend = (value, benchmark) => {
  if (value === null || value === undefined || benchmark === null || benchmark === undefined) {
    return { trend: null, trendDirection: null };
  }
  const diff = Math.round((value - benchmark) * 10) / 10;
  return {
    trend: Math.abs(diff),
    trendDirection: diff > 0 ? 'up' : (diff < 0 ? 'down' : 'same'),
  };
};

const feeLabel = computed(() => {
  const event = data.value?.event;
  if (!event || event.min_fee === null) {
    return '-';
  }
  const min = Number(event.min_fee).toLocaleString();
  if (event.has_multiple_fees && event.max_fee !== null && event.max_fee !== event.min_fee) {
    return `${min}〜${Number(event.max_fee).toLocaleString()}${$t('eventDetail.currencyUnit')}`;
  }
  return `${min}${$t('eventDetail.currencyUnit')}`;
});

/**
 * YYYY-MM-DD を YYYY年M月D日 表記へ
 */
const formatDateLabel = (date) => {
  if (!date) return '-';
  const [year, month, day] = date.split('-').map(Number);
  return $t('eventDetail.dateFormat', { year, month, day });
};

const dateRangeLabel = computed(() =>
  `${formatDateLabel(data.value?.event?.start_date)} 〜 ${formatDateLabel(data.value?.event?.end_date)}`
);

const capacityLabel = computed(() => {
  const capacity = data.value?.event?.capacity;
  return capacity != null ? capacity.toLocaleString() + $t('eventDetail.peopleUnit') : '-';
});

/**
 * 数字の連なりと単位(年月日・円・名など)に分割し、単位を小さく
 * グレーで表示するデザインを再現する
 */
const textParts = (text) => (String(text).match(/[\d,]+|[^\d,]+/g) || [])
  .map((part) => ({ text: part, isNum: /\d/.test(part) }));

const fetchStatistics = async () => {
  try {
    isLoading.value = true;
    const response = await axios.get(`/api/user/events/${route.params.id}/statistics`);
    data.value = response.data;
  } catch (error) {
    console.error('Error fetching event statistics:', error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchStatistics();
});
</script>

<style lang="scss" scoped>
.event-show {
  .outline-section {
    padding: 4px 4px 8px;
  }

  .edit-button {
    background-color: v-bind('colors.white');
    color: v-bind('colors.blue[500]');
    border: none;
    font-weight: 500;
    box-shadow: 0 1px 3px v-bind('colors.shadowColor');

    &:hover {
      background-color: v-bind('colors.gray[50]');
      color: v-bind('colors.blue[600]');
    }
  }

  .outline-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;

    .event-title {
      font-size: 24px;
      font-weight: 700;
      color: v-bind('colors.textPrimary');
      margin: 0 0 4px;
    }

    .organizer-name {
      font-size: 13px;
      color: v-bind('colors.gray[500]');
    }

    .edit-button {
      flex-shrink: 0;
      white-space: nowrap;
    }
  }

  .event-description {
    font-size: 14px;
    color: v-bind('colors.textPrimary');
    margin: 16px 0 0;
    white-space: pre-wrap;

    &.collapsed {
      display: -webkit-box;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  .description-toggle {
    display: inline-block;
    margin-top: 4px;
    font-size: 13px;
    color: v-bind('colors.btnBgColorPrimary');
    text-decoration: none;
  }

  .info-card {
    background: v-bind('colors.white');
    border: none;
    border-radius: 12px;
    padding: 12px 16px;
    box-shadow: 0 1px 3px v-bind('colors.shadowColor');
    height: 100%;

    .info-label {
      font-size: 12px;
      color: v-bind('colors.gray[500]');
      margin-bottom: 4px;
    }

    .info-value {
      font-size: 20px;
      font-weight: 700;
      color: v-bind('colors.textPrimary');

      .num {
        font-size: 20px;
        font-weight: 700;
        color: v-bind('colors.textPrimary');
      }

      .unit {
        font-size: 12px;
        font-weight: 500;
        color: v-bind('colors.gray[500]');
        margin: 0 2px;
      }
    }
  }
}
</style>
